/**
 * V2 admin · Tenant detail (full-page route).
 *
 * Reached from the Room detail "Current tenant" card. This route now renders
 * the unified tenant profile (the same component the Tenants list opens as a
 * modal) in full-page / expanded mode, so admins get the complete Overview,
 * Activity Timeline, Payments, Move-in/out and Documents in one place. Closing
 * it returns to the room — or, when opened from a colivIQ Brain card
 * (`?from=brain&item=`), to that card.
 */
import {
  useParams,
  useNavigate,
  useSearchParams,
  Link,
} from "react-router-dom";
import { AdminV2Layout } from "@/components/admin/v2/AdminV2Layout";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import {
  useBooking,
  useProfile,
  useProperty,
  useRoom,
} from "@/hooks/v2/useProperties";
import { useAuth } from "@/contexts/AuthContext";
import {
  TenantProfileDialog,
  type TenantProfileTenant,
  type TabId as TenantProfileTab,
} from "@/components/admin/v2/tenant/TenantProfileDialog";
import {
  invokeCreateContractForBooking,
  invokeSendContractToTenant,
} from "@/lib/adminCreateContract";
import { BrainReturnStrip } from "@/components/admin/brain/BrainReturnStrip";
import { brainReturnHref } from "@/lib/brain/deck";

// URL ?tab= values → profile TabId (accepts a couple of natural aliases).
const TAB_ALIASES: Record<string, TenantProfileTab> = {
  overview: "overview",
  payments: "payments",
  movein: "movein",
  "move-in": "movein",
  moveout: "moveout",
  "move-out": "moveout",
  documents: "documents",
};

export default function V2TenantDetail() {
  const navigate = useNavigate();
  const { isSuperAdmin, canManageTenant, user: currentUser } = useAuth();
  const { id, roomId, bookingId } = useParams<{
    id: string;
    roomId: string;
    bookingId: string;
  }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const initialTab = TAB_ALIASES[(searchParams.get("tab") || "").toLowerCase()];
  const propertyId = id ? Number(id) : null;
  const roomIdNum = roomId ? Number(roomId) : null;
  const bookingIdNum = bookingId ? Number(bookingId) : null;

  const backUrl =
    propertyId != null && roomIdNum != null
      ? `/admin/properties/${propertyId}/rooms/${roomIdNum}`
      : "/admin/properties";
  // Opened from a Brain card: the dialog covers the nav's return strip, so it
  // carries its own, and closing goes back to the card instead of the room.
  const brainHref = brainReturnHref(searchParams);

  const { data: booking, isLoading } = useBooking(bookingIdNum);
  const { data: profile } = useProfile(booking?.user_id ?? undefined);
  const { data: room } = useRoom(roomIdNum);
  const { data: property } = useProperty(propertyId);

  if (isLoading) {
    return (
      <AdminV2Layout title="Tenant">
        <div className="max-w-3xl mx-auto space-y-4 p-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </AdminV2Layout>
    );
  }

  if (!booking) {
    return (
      <AdminV2Layout title="Tenant">
        <div className="max-w-lg mx-auto text-center space-y-4 py-24">
          <h2 className="text-xl font-semibold">Tenant not found</h2>
          <p className="text-sm text-muted-foreground">
            This booking no longer exists or you don’t have access to it.
          </p>
          <Button asChild variant="outline">
            <Link to={backUrl}>Back to room</Link>
          </Button>
        </div>
      </AdminV2Layout>
    );
  }

  const p = profile as Record<string, any> | null | undefined;
  const tenant: TenantProfileTenant = {
    id: booking.id,
    userId: booking.user_id ?? null,
    booking,
    profile: p
      ? {
          id: p.id,
          name: p.full_name ?? p.name ?? null,
          full_name: p.full_name ?? null,
          email: p.email ?? null,
          phone: p.phone ?? null,
          date_of_birth: p.date_of_birth ?? null,
        }
      : null,
    room: room ?? null,
    property: property ?? null,
  };

  const roomTitle =
    (room as Record<string, any> | null)?.title ||
    (room as Record<string, any> | null)?.room_number ||
    "room";

  return (
    <TenantProfileDialog
      tenant={tenant}
      open
      initialExpanded
      initialTab={initialTab}
      banner={<BrainReturnStrip />}
      // The back-link closes the dialog, which goes to Brain in that mode —
      // a "‹ Room" label would lie, and the strip already says where it goes.
      backLabel={brainHref ? undefined : roomTitle}
      onTabChange={(t) => {
        // Keep the URL in sync so a refresh or shared link reopens the
        // same tab. Overview is the default, so drop the param for it. Only
        // `tab` changes — `from`/`item` must survive a tab switch.
        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            if (t === "overview") next.delete("tab");
            else next.set("tab", t);
            return next;
          },
          { replace: true }
        );
      }}
      onOpenChange={(o) => {
        if (!o) navigate(brainHref ?? backUrl);
      }}
      isSuperAdmin={isSuperAdmin}
      canManageTenant={canManageTenant}
      currentUserId={currentUser?.id ?? null}
      onGenerateContract={(bid, opts) =>
        invokeCreateContractForBooking(bid, opts).then(() => undefined)
      }
      onSendContract={(bid) =>
        invokeSendContractToTenant(bid).then(() => undefined)
      }
    />
  );
}
